import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Phone } from "lucide-react";
import api from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking"); // checking | paid | pending | failed
    const polls = useRef(0);
    useDocumentTitle("Payment Status · A1 Screening", "Confirming your A1 Screening payment.");

    useEffect(() => {
        if (!sessionId) { setState("failed"); return undefined; }
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            polls.current += 1;
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                const ps = r.data.payment_status;
                if (ps === "paid") { setState("paid"); return; }
                if (ps === "failed" || ps === "expired") { setState("failed"); return; }
            } catch { /* keep polling */ }
            if (polls.current >= MAX_POLLS) { setState("pending"); return; }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="a1-container a1-section max-w-lg text-center" data-testid="payment-success-page">
            {state === "checking" ? (
                <div className="a1-card py-12" data-testid="payment-checking">
                    <Clock size={36} className="mx-auto mb-3 animate-pulse" style={{ color: "var(--color-accent)" }} />
                    <h1 className="text-2xl mb-2">Confirming your payment…</h1>
                    <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                        One moment — we're checking with Stripe. Please don't close this page.
                    </p>
                </div>
            ) : state === "paid" ? (
                <div className="a1-card a1-card-feature py-12" data-testid="payment-paid">
                    <CheckCircle2 size={44} className="mx-auto mb-3" style={{ color: "var(--color-accent)" }} />
                    <h1 className="text-3xl mb-2">Payment received — thank you!</h1>
                    <p className="text-sm mb-6" style={{ color: "var(--color-text-muted)" }}>
                        Your invoice is marked paid and Stripe will email you a receipt. John will follow up on next steps.
                    </p>
                    <Link to="/" className="a1-btn-primary inline-flex" data-testid="payment-paid-home">Back to A1 Screening</Link>
                </div>
            ) : state === "pending" ? (
                <div className="a1-card py-12" data-testid="payment-pending">
                    <Clock size={36} className="mx-auto mb-3" style={{ color: "var(--color-warning)" }} />
                    <h1 className="text-2xl mb-2">Payment is processing</h1>
                    <p className="text-sm mb-6" style={{ color: "var(--color-text-muted)" }}>
                        Your payment is still being confirmed — you'll get a Stripe receipt by email once it completes.
                        If anything looks off, give us a call.
                    </p>
                    <a href="tel:+12394347306" className="a1-btn-secondary inline-flex"><Phone size={16} /> Call 239-434-7306</a>
                </div>
            ) : (
                <div className="a1-card py-12" data-testid="payment-failed">
                    <h1 className="text-2xl mb-2">We couldn't confirm that payment</h1>
                    <p className="text-sm mb-6" style={{ color: "var(--color-text-muted)" }}>
                        No worries — if you weren't charged, you can simply try again from your payment link.
                        Questions? Call us and we'll help right away.
                    </p>
                    <a href="tel:+12394347306" className="a1-btn-primary inline-flex"><Phone size={16} /> Call 239-434-7306</a>
                </div>
            )}
        </div>
    );
}
